import emailjs from '@emailjs/browser';

// EmailJS Configuration
// Sign up at https://www.emailjs.com/ (FREE - 200 emails/month)
// 1. Create an account at emailjs.com
// 2. Add an Email Service (Gmail, Outlook, etc.)
// 3. Create an Email Template
// 4. Get your Public Key from Account > API Keys

const EMAILJS_CONFIG = {
  serviceId: 'service_lsjdi59',
  templateId: 'template_pcxxmso',
  publicKey: 'wAq2vOydIVQdNAQLa'
};

// Default Company Information (fallback if not provided)
const DEFAULT_COMPANY_INFO = {
  name: 'Tax Services',
  phone: '',
  email: '',
  address: ''
};

// Initialize EmailJS
emailjs.init(EMAILJS_CONFIG.publicKey);

/**
 * Get day name from date string
 */
const getDayName = (dateString) => {
  // Add T00:00:00 to prevent timezone shift when parsing date
  const date = new Date(dateString + 'T00:00:00');
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[date.getDay()];
};

/**
 * Convert 24-hour time to 12-hour format
 */
const formatTime = (time24) => {
  if (!time24 || typeof time24 !== 'string') return 'TBD';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours);
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${minutes} ${period}`;
};

/**
 * Format date to readable format (e.g., "02/14/2026" - MM/DD/YYYY)
 */
const formatDate = (dateString) => {
  if (!dateString) return 'TBD';
  // Add T00:00:00 to prevent timezone shift when parsing date
  const date = new Date(dateString + 'T00:00:00');
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

/**
 * Generate confirmation email message - called when appointment is CONFIRMED
 * @param {Object} appointment - The appointment details
 * @param {Object} preparerDetails - The preparer's details
 * @param {Object} companyInfo - The company information
 */
export const generateConfirmationEmailMessage = (appointment, preparerDetails, companyInfo = {}) => {
  const company = { ...DEFAULT_COMPANY_INFO, ...companyInfo };
  const preparerPhone = preparerDetails?.phone || '';
  const preparerEmail = preparerDetails?.email || '';
  const preparerAddress = preparerDetails?.address || '';
  const numClients = parseInt(appointment.numberOfClients) || 1;
  
  // Build appointment details section based on single vs multi-client
  let appointmentDetailsSection = '';
  
  if (appointment.timeSlots && appointment.timeSlots.length > 0 && numClients > 1) {
    // Multi-client: show client count, date, and all times comma-separated
    appointmentDetailsSection += `👥 Number of Clients: ${numClients}`;
    
    // Group timeSlots by date for cleaner display
    const slotsByDate = {};
    appointment.timeSlots.forEach(slot => {
      const slotDate = typeof slot === 'object' ? slot.date : appointment.date;
      const slotTime = typeof slot === 'object' ? slot.time : slot;
      if (!slotsByDate[slotDate]) slotsByDate[slotDate] = [];
      slotsByDate[slotDate].push(slotTime);
    });
    
    const uniqueDates = Object.keys(slotsByDate);
    
    if (uniqueDates.length === 1) {
      // All clients on same date - show date once with all times
      const date = uniqueDates[0];
      const dayName = getDayName(date);
      const formattedDate = formatDate(date);
      const allTimes = slotsByDate[date].map(t => formatTime(t)).join(', ');
      appointmentDetailsSection += `\n📆 Date: ${dayName}, ${formattedDate}`;
      appointmentDetailsSection += `\n⏰ Time: ${allTimes}`;
    } else {
      // Different dates - show each date with its times
      uniqueDates.forEach(date => {
        const dayName = getDayName(date);
        const formattedDate = formatDate(date);
        const allTimes = slotsByDate[date].map(t => formatTime(t)).join(', ');
        appointmentDetailsSection += `\n📆 ${dayName}, ${formattedDate} at ${allTimes}`;
      });
    }
    
    appointmentDetailsSection += `\n👤 Tax Preparer: ${appointment.preparer}`;
  } else {
    // Single client (legacy or numberOfClients=1)
    const dayName = getDayName(appointment.date);
    const formattedDate = formatDate(appointment.date);
    const time = appointment.time || (appointment.timeSlots && appointment.timeSlots.length > 0 
      ? (typeof appointment.timeSlots[0] === 'object' ? appointment.timeSlots[0].time : appointment.timeSlots[0]) 
      : '');
    const formattedTime = formatTime(time);
    appointmentDetailsSection += `📆 Date: ${dayName}, ${formattedDate}`;
    appointmentDetailsSection += `\n⏰ Time: ${formattedTime}`;
    appointmentDetailsSection += `\n👤 Tax Preparer: ${appointment.preparer}`;
  }
  
  let message = `Dear ${appointment.fullName},

════════════════════════════════════════════
   🎉 YOUR APPOINTMENT IS CONFIRMED!
════════════════════════════════════════════

Great news! ${company.name} has confirmed your appointment booking.

📅 APPOINTMENT DETAILS
────────────────────────────────────────────
${appointmentDetailsSection}`;

  // Add preparer contact info and location if available
  if (preparerPhone || preparerEmail || preparerAddress) {
    message += `\n\n📞 PREPARER CONTACT & LOCATION`;
    message += `\n────────────────────────────────────────────`;
    if (preparerPhone) message += `\n📱 Phone: ${preparerPhone}`;
    if (preparerEmail) message += `\n📧 Email: ${preparerEmail}`;
    if (preparerAddress) message += `\n📍 Location: ${preparerAddress}`;
  }

  // Add company info
  message += `\n\n🏢 COMPANY INFORMATION`;
  message += `\n────────────────────────────────────────────`;
  message += `\n🏛️ ${company.name}`;
  if (company.phone) message += `\n📞 Phone: ${company.phone}`;
  if (company.email) message += `\n📧 Email: ${company.email}`;
  if (company.address) message += `\n📍 Address: ${company.address}`;

  message += `\n\n📝 IMPORTANT REMINDERS
────────────────────────────────────────────
✓ Please arrive 10 minutes early
✓ Bring all required documents
✓ Have your ID ready for verification

If you need to reschedule or cancel, please contact us as soon as possible.

We look forward to serving you!

Best regards,
${company.name}

════════════════════════════════════════════
This is an automated confirmation message.
Please do not reply directly to this email.
════════════════════════════════════════════`;

  return message;
};

/**
 * Generate email message (legacy function for backward compatibility)
 */
export const generateEmailMessage = (appointment, preparerDetails, companyInfo) => {
  return generateConfirmationEmailMessage(appointment, preparerDetails, companyInfo);
};

/**
 * Send appointment confirmation email to client when appointment is CONFIRMED
 * @param {Object} appointment - The appointment details
 * @param {Object} preparerDetails - The preparer's details (optional)
 * @param {Object} companyInfo - The company information (optional - name, phone, email, address)
 * @returns {Promise} - EmailJS send promise
 */
export const sendConfirmationEmail = async (appointment, preparerDetails = null, companyInfo = null) => {
  const company = { ...DEFAULT_COMPANY_INFO, ...companyInfo };
  
  // Generate the email message
  const emailMessage = generateConfirmationEmailMessage(appointment, preparerDetails, company);
  
  // Template params - EmailJS template should have {{to_email}}, {{to_name}}, {{from_name}}, {{subject}}, {{message}}
  const templateParams = {
    to_email: String(appointment.email || ''),
    to_name: String(appointment.fullName || ''),
    from_name: String(company.name || 'Tax Services'),
    reply_to: String(company.email || ''),
    subject: String(`✅ Appointment Confirmed - ${company.name}`),
    message: String(emailMessage)
  };

  try {
    // Check if EmailJS is configured
    if (EMAILJS_CONFIG.serviceId === 'YOUR_SERVICE_ID') {
      return { status: 200, text: 'EMAIL_NOT_CONFIGURED', message: emailMessage };
    }

    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templateId,
      templateParams,
      EMAILJS_CONFIG.publicKey
    );
    
    return { ...response, message: emailMessage };
  } catch (error) {
    console.error('❌ Failed to send confirmation email:', error);
    console.error('❌ Error status:', error.status);
    console.error('❌ Error text:', error.text);
    throw error;
  }
};

/**
 * Generate reminder email message
 */
const generateReminderEmailMessage = (appointment, preparerDetails, companyInfo = {}) => {
  const company = { ...DEFAULT_COMPANY_INFO, ...companyInfo };
  const preparerPhone = preparerDetails?.phone || '';
  const preparerEmail = preparerDetails?.email || '';
  const preparerAddress = preparerDetails?.address || '';
  const numClients = parseInt(appointment.numberOfClients) || 1;
  
  // Build appointment details section based on single vs multi-client
  let appointmentDetailsSection = '';
  
  if (appointment.timeSlots && appointment.timeSlots.length > 0 && numClients > 1) {
    // Multi-client: show client count, date, and all times comma-separated
    appointmentDetailsSection += `👥 Number of Clients: ${numClients}`;
    
    const slotsByDate = {};
    appointment.timeSlots.forEach(slot => {
      const slotDate = typeof slot === 'object' ? slot.date : appointment.date;
      const slotTime = typeof slot === 'object' ? slot.time : slot;
      if (!slotsByDate[slotDate]) slotsByDate[slotDate] = [];
      slotsByDate[slotDate].push(slotTime);
    });
    
    const uniqueDates = Object.keys(slotsByDate);
    
    if (uniqueDates.length === 1) {
      const date = uniqueDates[0];
      const dayName = getDayName(date);
      const formattedDate = formatDate(date);
      const allTimes = slotsByDate[date].map(t => formatTime(t)).join(', ');
      appointmentDetailsSection += `\n📆 Date: ${dayName}, ${formattedDate}`;
      appointmentDetailsSection += `\n⏰ Time: ${allTimes}`;
    } else {
      uniqueDates.forEach(date => {
        const dayName = getDayName(date);
        const formattedDate = formatDate(date);
        const allTimes = slotsByDate[date].map(t => formatTime(t)).join(', ');
        appointmentDetailsSection += `\n📆 ${dayName}, ${formattedDate} at ${allTimes}`;
      });
    }
    
    appointmentDetailsSection += `\n👤 Tax Preparer: ${appointment.preparer}`;
  } else {
    const dayName = getDayName(appointment.date);
    const formattedDate = formatDate(appointment.date);
    const time = appointment.time || (appointment.timeSlots && appointment.timeSlots.length > 0 
      ? (typeof appointment.timeSlots[0] === 'object' ? appointment.timeSlots[0].time : appointment.timeSlots[0]) 
      : '');
    const formattedTime = formatTime(time);
    appointmentDetailsSection += `📆 Date: ${dayName}, ${formattedDate}`;
    appointmentDetailsSection += `\n⏰ Time: ${formattedTime}`;
    appointmentDetailsSection += `\n👤 Tax Preparer: ${appointment.preparer}`;
  }
  
  let message = `Dear ${appointment.fullName},

════════════════════════════════════════════
   ⏰ APPOINTMENT REMINDER
════════════════════════════════════════════

This is a friendly reminder about your upcoming appointment with ${company.name}.

📅 APPOINTMENT DETAILS
────────────────────────────────────────────
${appointmentDetailsSection}`;

  if (preparerPhone || preparerEmail || preparerAddress) {
    message += `\n\n📞 PREPARER CONTACT & LOCATION`;
    message += `\n────────────────────────────────────────────`;
    if (preparerPhone) message += `\n📱 Phone: ${preparerPhone}`;
    if (preparerEmail) message += `\n📧 Email: ${preparerEmail}`;
    if (preparerAddress) message += `\n📍 Location: ${preparerAddress}`;
  }

  message += `\n\n🏢 COMPANY INFORMATION`;
  message += `\n────────────────────────────────────────────`;
  message += `\n🏛️ ${company.name}`;
  if (company.phone) message += `\n📞 Phone: ${company.phone}`;
  if (company.email) message += `\n📧 Email: ${company.email}`;
  if (company.address) message += `\n📍 Address: ${company.address}`;

  message += `\n\n📝 PLEASE REMEMBER
────────────────────────────────────────────
✓ Please arrive 10 minutes early
✓ Bring all required documents
✓ Have your ID ready for verification

If you need to reschedule or cancel, please contact us as soon as possible.

We look forward to seeing you!

Best regards,
${company.name}

════════════════════════════════════════════
This is an automated reminder message.
════════════════════════════════════════════`;

  return message;
};

/**
 * Send appointment reminder email to client
 * @param {Object} appointment - The appointment details
 * @param {Object} preparerDetails - The preparer's details (optional)
 * @param {Object} companyInfo - The company information (optional)
 * @returns {Promise} - EmailJS send promise
 */
export const sendReminderEmail = async (appointment, preparerDetails = null, companyInfo = null) => {
  const company = { ...DEFAULT_COMPANY_INFO, ...companyInfo };
  
  const emailMessage = generateReminderEmailMessage(appointment, preparerDetails, company);
  
  const templateParams = {
    to_email: String(appointment.email || ''),
    to_name: String(appointment.fullName || ''),
    from_name: String(company.name || 'Tax Services'),
    reply_to: String(company.email || ''),
    subject: String(`⏰ Appointment Reminder - ${company.name}`),
    message: String(emailMessage)
  };

  try {
    if (EMAILJS_CONFIG.serviceId === 'YOUR_SERVICE_ID') {
      return { status: 200, text: 'EMAIL_NOT_CONFIGURED', message: emailMessage };
    }

    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templateId,
      templateParams,
      EMAILJS_CONFIG.publicKey
    );
    
    return { ...response, message: emailMessage };
  } catch (error) {
    console.error('❌ Failed to send reminder email:', error);
    throw error;
  }
};

/**
 * Generate tax season reminder email for past clients
 */
const generateTaxSeasonEmailMessage = (clientName, preparerDetails, companyInfo, bookingLink) => {
  const company = { ...DEFAULT_COMPANY_INFO, ...companyInfo };
  const preparerName = preparerDetails?.name || 'your tax preparer';
  
  let message = `Dear ${clientName},

════════════════════════════════════════════
   📅 TAX SEASON 2026 IS HERE!
════════════════════════════════════════════

It's time to book your tax appointment for the 2025 tax year!

👤 YOUR TAX PREPARER
────────────────────────────────────────────
${preparerName} is ready to assist you with your tax return this year.`;

  if (preparerDetails?.phone || preparerDetails?.email) {
    message += `\n\n📞 CONTACT INFORMATION`;
    message += `\n────────────────────────────────────────────`;
    if (preparerDetails?.phone) message += `\n📱 Phone: ${preparerDetails.phone}`;
    if (preparerDetails?.email) message += `\n📧 Email: ${preparerDetails.email}`;
  }

  if (bookingLink) {
    message += `\n\n🗓️ BOOK YOUR APPOINTMENT`;
    message += `\n────────────────────────────────────────────`;
    message += `\nClick here to schedule: ${bookingLink}`;
  }

  message += `\n\n🏢 ${company.name}`;
  if (company.phone) message += `\n📞 Phone: ${company.phone}`;
  if (company.email) message += `\n📧 Email: ${company.email}`;
  if (company.address) message += `\n📍 Address: ${company.address}`;

  message += `\n\n📝 WHAT TO PREPARE
────────────────────────────────────────────
✓ W-2 forms from all employers
✓ 1099 forms (if applicable)
✓ Social Security numbers for all dependents
✓ Last year's tax return for reference
✓ Valid ID

Don't wait - book early to get your preferred time slot!

Best regards,
${company.name}

════════════════════════════════════════════
This is an automated message from ${company.name}.
════════════════════════════════════════════`;

  return message;
};

/**
 * Send tax season reminder email to past clients
 * @param {Object} client - Client info with email, fullName
 * @param {Object} preparerDetails - Preparer's details (name, phone, email)
 * @param {Object} companyInfo - Company information
 * @param {string} bookingLink - Link to book appointment
 * @returns {Promise} - EmailJS send promise
 */
export const sendTaxSeasonEmail = async (client, preparerDetails = null, companyInfo = null, bookingLink = null) => {
  const company = { ...DEFAULT_COMPANY_INFO, ...companyInfo };
  
  const emailMessage = generateTaxSeasonEmailMessage(client.fullName, preparerDetails, company, bookingLink);
  
  const templateParams = {
    to_email: String(client.email || ''),
    to_name: String(client.fullName || ''),
    from_name: String(company.name || 'Tax Services'),
    reply_to: String(company.email || ''),
    subject: String(`📅 Tax Season 2026 - Book Your Appointment with ${preparerDetails?.name || company.name}`),
    message: String(emailMessage)
  };

  try {
    if (EMAILJS_CONFIG.serviceId === 'YOUR_SERVICE_ID') {
      return { status: 200, text: 'EMAIL_NOT_CONFIGURED', message: emailMessage };
    }

    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templateId,
      templateParams,
      EMAILJS_CONFIG.publicKey
    );
    
    return { ...response, message: emailMessage };
  } catch (error) {
    console.error('❌ Failed to send tax season email:', error);
    throw error;
  }
};

/**
 * Update EmailJS configuration
 * Call this function after getting your EmailJS credentials
 */
export const configureEmailJS = (serviceId, templateId, publicKey) => {
  EMAILJS_CONFIG.serviceId = serviceId;
  EMAILJS_CONFIG.templateId = templateId;
  EMAILJS_CONFIG.publicKey = publicKey;
  emailjs.init(publicKey);
};

/**
 * Update company information (legacy - not needed if passing companyInfo to sendConfirmationEmail)
 */
export const updateCompanyInfo = (name, phone, email, address) => {
  DEFAULT_COMPANY_INFO.name = name || DEFAULT_COMPANY_INFO.name;
  DEFAULT_COMPANY_INFO.phone = phone || DEFAULT_COMPANY_INFO.phone;
  DEFAULT_COMPANY_INFO.email = email || DEFAULT_COMPANY_INFO.email;
  DEFAULT_COMPANY_INFO.address = address || DEFAULT_COMPANY_INFO.address;
};

/**
 * Generate consent request email message
 * @param {Object} params - Email parameters
 * @param {string} params.taxpayerName - Taxpayer's name
 * @param {string} params.preparerName - Preparer's name
 * @param {string} params.companyName - Company name
 * @param {string} params.consentLink - Link to consent form
 * @param {number} params.taxYear - Tax year
 */
export const generateConsentEmailMessage = ({ taxpayerName, preparerName, companyName, consentLink, taxYear }) => {
  return `Dear ${taxpayerName},

════════════════════════════════════════════
   📋 IRS WAIVER FORM - ACTION REQUIRED
════════════════════════════════════════════

${companyName} needs you to complete your ${taxYear} IRS Waiver and Client Information form.

Please click the link below to complete and sign the required documents:

🔗 ${consentLink}

────────────────────────────────────────────

This form includes:
✅ IRS Notice 2014-7 Acknowledgment & Election
✅ Client Information (Taxpayer, Spouse, Dependents)
✅ Photo ID Upload for Verification
✅ Engagement Letter Agreement
✅ Electronic Signature (Draw, Type, or Scan QR Code)

────────────────────────────────────────────

⏰ Please complete this form as soon as possible.

If you have any questions, please contact your tax preparer:
👤 ${preparerName}
🏢 ${companyName}

Thank you for choosing ${companyName} for your tax preparation needs!

────────────────────────────────────────────
This is an automated message. Please do not reply directly to this email.
`;
};

/**
 * Send consent request email to taxpayer
 * @param {string} toEmail - Recipient email
 * @param {string} toName - Recipient name
 * @param {string} subject - Email subject
 * @param {Object} consentData - Consent form data
 * @param {Object} companyInfo - Company information
 */
export const sendConsentEmail = async (toEmail, toName, subject, consentData, companyInfo = {}) => {
  const company = { ...DEFAULT_COMPANY_INFO, ...companyInfo };
  
  const emailMessage = generateConsentEmailMessage({
    taxpayerName: toName,
    preparerName: consentData.preparerName,
    companyName: company.name,
    consentLink: consentData.consentLink,
    taxYear: consentData.taxYear || new Date().getFullYear() - 1
  });

  const templateParams = {
    to_email: toEmail,
    to_name: toName,
    from_name: company.name,
    reply_to: company.email || toEmail,
    subject: subject || `Action Required: Tax Consent Form - ${company.name}`,
    message: String(emailMessage)
  };

  try {
    if (EMAILJS_CONFIG.serviceId === 'YOUR_SERVICE_ID') {
      console.log('Email not configured. Email content:', emailMessage);
      return { status: 200, text: 'EMAIL_NOT_CONFIGURED', message: emailMessage };
    }

    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templateId,
      templateParams,
      EMAILJS_CONFIG.publicKey
    );
    
    return { ...response, message: emailMessage };
  } catch (error) {
    console.error('❌ Failed to send consent email:', error);
    throw error;
  }
};

// Named export object for better compatibility
const emailService = { 
  sendConfirmationEmail, 
  sendConsentEmail,
  configureEmailJS, 
  generateEmailMessage,
  generateConfirmationEmailMessage,
  generateConsentEmailMessage,
  updateCompanyInfo 
};

export default emailService;
